import React from 'react';
import { Modal, message } from 'antd';
import ResumeSection from '../components/ResumeSection';
import IntentionsView from '../com/IntentionsView';
import JobSelectModal from '../components/JobSelectModal';
import AreaSelectModal from '../components/AreaSelectModal';
import { intentionSection } from '../configs/sectionConfig';
import { buildIntention } from '../utils/intentionTransforms';
import Config from '../../../config/Config';
import WidgetAjax from '../../../widget/WidgetAjax';

const maxCount = intentionSection.maxCount || 5;

const initialSelectorState = {
  jobModalVisible: false,
  areaModalVisible: false,
  selectorTarget: undefined,
  selectorRecordIndex: undefined,
  // 弹窗内"已选"仅用于回显弹窗, 不再写回 IntentionModule 的"主真值" (item.JOBTYPE)
  selectedJobCodes: [],
  selectedJobItems: [],
  selectedAreaCodes: [],
  selectedAreaItems: [],
};


class IntentionModule extends React.Component {
  formRef = React.createRef();

  // 本地草稿 items: 直接放 item.JOBTYPE = { name, value } / item.DISTRICT = { name, value }
  // 不再需要 _pendingPatch (旧版本需要三组平行 state 合并, 现在不需要了)
  _pendingItems = undefined;

  constructor(props) {
    super(props);
    this.state = {
      editingIndex: undefined,
      adding: false,
      selectorState: { ...initialSelectorState },
      _pendingVersion: 0,
    };
  }

  componentDidUpdate(prevProps) {
    // server 数据 (resumeState) 变化时, 重置本地草稿
    const prevList = this._extractIntentions(prevProps.resumeState);
    const nextList = this._extractIntentions(this.props.resumeState);
    if (prevList !== nextList) {
      this._pendingItems = undefined;
    }
  }


  _extractIntentions = (resumeState) => {
    if (!resumeState) return [];
    const raw = resumeState.raw || {};
    if (Array.isArray(raw.intentions)) return raw.intentions;
    if (raw.intention && typeof raw.intention === 'object') return [raw.intention];
    return [];
  };

  /**
   * 从 props.resumeState 派生当前 server 上的展示数据
   * 后端返回的 JOBTYPE / DISTRICT 已经是 [{ name, value }] 数组,
   * 取 [0] 整个对象存进 item — 这就是"对象形态"的来源.
   */
  deriveFromProps = () => {
    const { resumeState } = this.props;
   
    const list = this._extractIntentions(resumeState);
    const items = list.map((it, idx) => {
      const firstJob = (it.JOBTYPE || [])[0];
      const firstDistrict = (it.DISTRICT || [])[0];
      return {
        id: it.id,
        // 对象形态: name + value 完整保留
        JOBTYPE: firstJob ? { name: firstJob.name, value: firstJob.value || firstJob.code } : undefined,
        DISTRICT: firstDistrict ? { name: firstDistrict.name, value: firstDistrict.value || firstDistrict.code } : undefined,
        PROPERTY: it.PROPERTY && it.PROPERTY[0] ? it.PROPERTY[0].value : undefined,
        SALFROM: it.SALFROM ? Number(it.SALFROM) : 0,
        SALTO: it.SALTO ? Number(it.SALTO) : 0,
        _key: `init-${idx}`,
      };
    });
    return { items };
  };

  // ============ 编辑态 ============

  startEdit = (idx) => {
    this._pendingItems = undefined;
    this.setState({ editingIndex: idx, adding: false });
  };

  startAdd = () => {
    const derived = this.deriveFromProps();
    const currentCount = this._pendingItems
      ? this._pendingItems.length
      : derived.items.length;
    if (currentCount >= maxCount) {
      message.warning(`最多只能添加 ${maxCount} 条求职意向`);
      return;
    }
    this._pendingItems = undefined;
    this.setState({ editingIndex: undefined, adding: true });
  };

  closeEditor = () => {
    this.setState({ editingIndex: undefined, adding: false });
  };

  /**
   * IntentionsView.onAdd 路由: edit / add / remove
   * - edit: 进入编辑态
   * - add: 进入添加态 (受 maxCount 限制)
   * - remove: 已入库条目走 /applicant/remove_intention; 本地草稿直接清掉
   */
  handleOnAdd = (action, idx) => {
    if (action === 'edit') this.startEdit(idx);
    else if (action === 'add') this.startAdd();
    else if (action === 'remove') this.removeIntention(idx);
  };

  removeIntention = (idx) => {
    const derived = this.deriveFromProps();
    const items = this._pendingItems !== undefined ? this._pendingItems : derived.items;
    const item = items[idx];
    // 本地草稿 (没有 id) 直接清掉, 不弹 modal
    if (!item || !item.id) {
      this.setState((prev) => {
        const nextItems = items.filter((_, i) => i !== idx);
        this._pendingItems = nextItems;
        return { ...prev, _pendingVersion: prev._pendingVersion + 1 };
      });
      return;
    }

    Modal.confirm({
      title: '确认删除',
      content: '确定删除这条求职意向吗？',
      okText: '确认',
      cancelText: '取消',
      onOk: () => this.handleRemoveRemote(item),
    });
  };

  handleRemoveRemote = (record) => {
    const { applicantId, onChanged } = this.props;
    WidgetAjax.ajax({
      url: `${Config.getBasePath()}/applicant/remove_intention`,
      params: { id: record.id, applicant_id: applicantId },
      callback: (res) => {
        if (res && res.status === 0) {
          message.success('删除成功');
          if (typeof onChanged === 'function') onChanged();
          return;
        }
        message.error((res && res.message) || '删除失败');
      },
    });
  };

  // ============ 表单提交 (IntentionsView.onAfterEdit) ============

  /**
   * payload: { item, isNew }   (v2 删了 selectedJob / selectedArea)
   * - isNew === true  → /applicant/add_intention
   * - isNew === false → /applicant/update_intention (需 item.id)
   * item.JOBTYPE / item.DISTRICT 现在都是 { name, value } 对象,
   * buildIntention 内部取 .value 包成 [value] 数组.
   *
   * 关键约定:
   *   - IntentionsView 已经在 onAfterEdit 调用之前就调了 onCancel() 关闭编辑态,
   *     这里只需关注 API 调用本身.
   *   - status === 0 才触发 onChanged (父页面 reloadResume);
   *     失败只 toast, 不调 onChanged.
   */
  handleAfterEdit = (payload) => {
    if (typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.log('[IntentionModule] handleAfterEdit called', { payload, applicantId: this.props.applicantId });
    }
    if (!payload || !payload.item) return;

    const { applicantId, onChanged } = this.props;
    // 防御: 编辑时如果 payload.item.id 缺失 (例如老数据 / 脏数据 / 闭包失效),
    // 尝试从 deriveFromProps 里再找一个 id 兜底, 否则不让接口"无声不发"
    let itemForApi = payload.item;
    if (!payload.isNew && !payload.item.id) {
      const derived = this.deriveFromProps();
      // 不一定能精确定位 idx, 但 derived.items 是 server 真值, 至少能拿到 id 列表
      const fallback = derived.items.find((it) => it && it.id && (it.JOBTYPE?.value || it.DISTRICT?.value));
      if (fallback && fallback.id) {
        // 找到 fallback: 把 payload.item 的对象字段对齐 (但保留 form values)
        itemForApi = { ...fallback, ...payload.item, id: fallback.id };
      }
    }
    const payloadList = buildIntention([itemForApi], [], []);
    const single = payloadList[0] || {};

    if (payload.isNew) {
    
      WidgetAjax.ajax({
        url: `${Config.getBasePath()}/applicant/add_intention`,
        params: { applicant_id: applicantId, ...single },
        callback: (res) => {
          if (res && res.status === 0) {
            message.success('添加成功');
            this._pendingItems = undefined;
            this.closeEditor();
            if (typeof onChanged === 'function') onChanged();
            return;
          }
          // 失败回滚: 清掉 patch, render 回到 server 原貌
          this._pendingItems = undefined;
          message.error((res && res.message) || '添加失败');
        },
      });
      return;
    }

    if (!itemForApi.id) {
      message.error('缺少意向 id, 无法更新');
      return;
    }
    WidgetAjax.ajax({
      url: `${Config.getBasePath()}/applicant/update_intention`,
      params: { id: itemForApi.id, applicant_id: applicantId, ...single },
      callback: (res) => {
        if (res && res.status === 0) {
          message.success('保存成功');
          this._pendingItems = undefined;
          this.closeEditor();
          if (typeof onChanged === 'function') onChanged();
          return;
        }
        this._pendingItems = undefined;
        message.error((res && res.message) || '保存失败');
      },
    });
  };

  // ============ 字段触发 → 打开选择器 ============

  /**
   * 在 ADD 模式下, 即便 list.length 已经被前一次弹窗写入改了, 后续弹窗仍然要写
   * 同一个 draft 槽位. 这里把 recordIndex 修正成"真正的 draft 位置":
   *   - ADD 模式 + 列表末尾已有 draft (无 id)  → 用末尾索引
   *   - 其他情况                              → 用 onTrigger 传入的 recordIndex
   */
  resolveRecordIndex = (recordIndex) => {
    if (this.state.adding && this._pendingItems && this._pendingItems.length > 0) {
      const lastIdx = this._pendingItems.length - 1;
      const lastItem = this._pendingItems[lastIdx];
      if (lastItem && !lastItem.id) return lastIdx;
    }
    return recordIndex;
  };

  /**
   * IntentionsView.onTrigger 路由:
   *   - intentionJob   → JobSelectModal (single, thirdLevelOnly)
   *   - intentionArea  → AreaSelectModal (allowSecondLevel)
   */
  handleFieldTrigger = (field, recordIndex) => {
    if (field.trigger === 'intentionJob') {
      const targetIndex = this.resolveRecordIndex(recordIndex);
      const derived = this.deriveFromProps();
      const items = this._pendingItems !== undefined ? this._pendingItems : derived.items;
      const current = items[targetIndex] && items[targetIndex].JOBTYPE;
      this.openSelectorModal(
        'intentionJob',
        targetIndex,
        current ? [current.value || current.code] : [],
        current ? [current] : []
      );
    } else if (field.trigger === 'intentionArea') {
      const targetIndex = this.resolveRecordIndex(recordIndex);
      const derived = this.deriveFromProps();
      const items = this._pendingItems !== undefined ? this._pendingItems : derived.items;
      const current = items[targetIndex] && items[targetIndex].DISTRICT;
      this.openSelectorModal(
        'intentionArea',
        targetIndex,
        current ? [current.value || current.code] : [],
        current ? [current] : []
      );
    }
  };

  openSelectorModal = (target, recordIndex, selectedCodes, selectedItems) => {
    this.setState((prev) => {
      const base = { ...prev.selectorState };
      if (target === 'intentionJob') {
        return {
          ...prev,
          selectorState: {
            ...base,
            selectorTarget: 'intentionJob',
            jobModalVisible: true,
            selectorRecordIndex: recordIndex,
            selectedJobCodes: selectedCodes || [],
            selectedJobItems: selectedItems || [],
          },
        };
      }
      if (target === 'intentionArea') {
        return {
          ...prev,
          selectorState: {
            ...base,
            selectorTarget: 'intentionArea',
            areaModalVisible: true,
            selectorRecordIndex: recordIndex,
            selectedAreaCodes: selectedCodes || [],
            selectedAreaItems: selectedItems || [],
          },
        };
      }
      return prev;
    });
  };

  closeSelectors = () => {
    this.setState((prev) => ({
      ...prev,
      selectorState: {
        ...prev.selectorState,
        jobModalVisible: false,
        areaModalVisible: false,
        selectorTarget: undefined,
      },
    }));
  };

  // ============ 选择器确认 → 直接写 item.JOBTYPE / item.DISTRICT 对象 ============

  /**
   * 关键修复: 弹窗确认后, 选中的对象直接写进 item.JOBTYPE / item.DISTRICT.
   * 不再走"selectedJobs[idx] = { code, name }" 平行 state 中转.
   *
   * 注意: 编辑/添加模式下, IntentionView 的 list[idx] 是当前的 item.
   *   - 编辑: list[idx] = list[editingIndex] (已经存在)
   *   - 添加: list[list.length] 是 undefined, 我们在添加模式下需要一个"草稿位"
   *          —— 由 setItems 写一个空对象 { _key: 'draft' } 占位, 等弹窗写入后回填.
   */
  handleJobConfirm = (items) => {
   
    const { selectorRecordIndex } = this.state.selectorState;
    const item = items[0];

    // 1) 写表单字段 (JOBTYPE 字符串) — 让 inputBlur 显示中文名
    if (this.formRef.current && item) {
      this.formRef.current.setFieldValue('JOBTYPE', item.name);
    }

    // 2) 写 item.JOBTYPE = { name, value } 对象 (添加/编辑都用同一路径)
    if (typeof selectorRecordIndex === 'number') {
      const derived = this.deriveFromProps();
      const baseItems = this._pendingItems !== undefined ? this._pendingItems : derived.items;
      const nextItems = [...baseItems];

      // 添加模式: list[list.length] 不存在, 补一个 draft 占位
      if (selectorRecordIndex >= nextItems.length) {
        nextItems.push({ _key: `draft-${Date.now()}` });
      }
      nextItems[selectorRecordIndex] = {
        ...(nextItems[selectorRecordIndex] || {}),
        JOBTYPE: item ? { name: item.name, value: item.code || item.value } : undefined,
      };
      this._pendingItems = nextItems;
      this.setState((prev) => ({ ...prev, _pendingVersion: prev._pendingVersion + 1 }));
    }

    this.closeSelectors();
  };

  handleAreaConfirm = (items) => {
    const { selectorRecordIndex } = this.state.selectorState;
    const item = items[0];
    const districtName = item ? item.name : '';

    // 1) 写表单字段 (DISTRICT 字符串) — 让 inputBlur 显示中文名
    if (this.formRef.current) {
      this.formRef.current.setFieldValue('DISTRICT', districtName);
    }

    // 2) 写 item.DISTRICT = { name, value } 对象
    if (typeof selectorRecordIndex === 'number') {
      const derived = this.deriveFromProps();
      const baseItems = this._pendingItems !== undefined ? this._pendingItems : derived.items;
      const nextItems = [...baseItems];

      if (selectorRecordIndex >= nextItems.length) {
        nextItems.push({ _key: `draft-${Date.now()}` });
      }
      nextItems[selectorRecordIndex] = {
        ...(nextItems[selectorRecordIndex] || {}),
        DISTRICT: item ? { name: item.name, value: item.code || item.value } : undefined,
      };
      this._pendingItems = nextItems;
      this.setState((prev) => ({ ...prev, _pendingVersion: prev._pendingVersion + 1 }));
    }

    this.closeSelectors();
  };

  // ============ IntentionsView 所需的 setState 包装器 ============

  setItems = (updater) => {
    const derived = this.deriveFromProps();
    const baseItems = this._pendingItems !== undefined ? this._pendingItems : derived.items;
    const newItems = typeof updater === 'function' ? updater(baseItems) : updater;
    this._pendingItems = newItems;
    this.setState((prev) => ({ ...prev, _pendingVersion: prev._pendingVersion + 1 }));
  };

  // certificate / works 是其它 section 的 state, 求职意向不涉及, 传 no-op 避免破坏 ResumeEditorForm 字段.
  setCertificateFile = () => {};
  setWorksFile = () => {};

  // ============ 渲染 ============

  render() {
    const { options, applicantId, reloadJobTypes } = this.props;
    const { editingIndex, adding, selectorState } = this.state;
    const { jobTypeList = [], areaList = [] } = options || {};

    const derived = this.deriveFromProps();
    const items = this._pendingItems !== undefined ? this._pendingItems : derived.items;

    return (
      <>
        <ResumeSection
          section={intentionSection}
          editing={typeof editingIndex === 'number'}
          adding={adding}
        >
          <IntentionsView
            section={intentionSection}
            items={items}
            setItems={this.setItems}
            editingIndex={editingIndex}
            adding={adding}
            options={options}
            applicantId={applicantId}
            formRef={this.formRef}
            setCertificateFile={this.setCertificateFile}
            setWorksFile={this.setWorksFile}
            onTrigger={this.handleFieldTrigger}
            onCancel={this.closeEditor}
            onAdd={this.handleOnAdd}
            onAfterEdit={this.handleAfterEdit}
            maxCount={maxCount}
          />
        </ResumeSection>

        {/* 岗位选择器 (求职意向): single + thirdLevelOnly */}
        <JobSelectModal
          single
          thirdLevelOnly
          visible={
            selectorState.jobModalVisible
            && selectorState.selectorTarget === 'intentionJob'
          }
          jobTypeList={jobTypeList}
          selectedCodes={selectorState.selectedJobCodes}
          selectedItems={selectorState.selectedJobItems}
          onSearch={reloadJobTypes}
          onCancel={this.closeSelectors}
          onConfirm={this.handleJobConfirm}
        />

        {/* 地区选择器 (求职意向): allowSecondLevel */}
        <AreaSelectModal
          allowSecondLevel
          visible={
            selectorState.areaModalVisible
            && selectorState.selectorTarget === 'intentionArea'
          }
          areaList={areaList}
          selectedCodes={selectorState.selectedAreaCodes}
          selectedItems={selectorState.selectedAreaItems}
          onCancel={this.closeSelectors}
          onConfirm={this.handleAreaConfirm}
        />
      </>
    );
  }
}

export default IntentionModule;
